// src/pages/HomePage.jsx
import React, {
  useEffect,
  useState,
  Suspense,
  lazy,
  useRef,
} from "react";
import { Helmet } from "react-helmet";
import "./HomePage.css";

import SiteHeader from "../components/SiteHeader";
import Footer from "../components/Footer";
import ParallaxHero from "../components/ParallaxHero";
import ManuscriptCard from "../components/ManuscriptCard";
import ExternalLinks from "../components/ExternalLinks"; // ✅ Import Component ใหม่ที่นี่

import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { fetchItemsLite } from "../lib/omekaClient";
import { Search, ArrowRight, Book } from "lucide-react";

// ⛱ lazy-load components
const AutoCarousel = lazy(() => import("../components/AutoCarousel"));
const ArticlesBlock = lazy(() => import("../components/ArticlesBlock"));

export default function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const searchRef = useRef(null);

  // datasets
  const [books, setBooks] = useState([]);
  const [manuscripts, setManuscripts] = useState([]);
  const [articles, setArticles] = useState([]);

  // State สำหรับใบลานและสมุดไทย
  const [palmLeaves, setPalmLeaves] = useState([]);
  const [samutThais, setSamutThais] = useState([]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [query, setQuery] = useState("");

  const openDetail = (item) => {
    const id = item?.["o:id"];
    if (!id) return;
    navigate(`/book/${id}`);
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    if (query.trim()) {
      navigate(`/books?q=${encodeURIComponent(query.trim())}&page=1`);
    }
  };

  // โหลดข้อมูลจาก Omeka S
  useEffect(() => {
    if (window.snapSaveState) window.snapSaveState = () => { };

    (async () => {
      try {
        setLoading(true);
        const [bks, manus, arts, palms, samuts] = await Promise.all([
          fetchItemsLite({ limit: 6, resource_class_label: "Book" }),
          fetchItemsLite({ limit: 10, resource_class_label: "Manuscript" }),
          fetchItemsLite({ limit: 6, resource_class_label: "Article" }),
          fetchItemsLite({ limit: 3, item_set_id: 55 }), // คัมภีร์ใบลาน (id55)
          fetchItemsLite({ limit: 3, item_set_id: 56 }), // หนังสือสมุดไทย (id56)
        ]);

        setBooks(Array.isArray(bks) ? bks : []);
        setManuscripts(Array.isArray(manus) ? manus : []);
        setArticles(Array.isArray(arts) ? arts : []);
        setPalmLeaves(Array.isArray(palms) ? palms : []);
        setSamutThais(Array.isArray(samuts) ? samuts : []);
      } catch (e) {
        setErr(e?.message || t("errors.loadFailed"));
      } finally {
        setLoading(false);
        if (window.snapSaveState) {
          setTimeout(() => { window.snapSaveState = null; }, 800);
        }
      }
    })();
  }, [i18n.language, t]);

  const shortcuts = [
    { label: t("home.shortcuts.palmLeaf"), path: "/books/palmleaf?&page=1" },
    { label: t("home.shortcuts.samutThai"), path: "/books/samutthai?&page=1" },
    { label: t("home.shortcuts.rareBooks"), path: "/books/rare?&page=1" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#fcfaf8] text-[#3d342e] font-sans">
      <Helmet>
        <title>{t("meta.homeTitle", "หน้าหลัก | E-Book Lanna Digital Library")}</title>
        <meta
          name="description"
          content={t("home.meta.description")}
        />
      </Helmet>

      <SiteHeader />

      <ParallaxHero
        banner="/assets/hero-bg.jpg"
        title={
          <>
            {t("home.hero.titleLine1")}
            <br />
            {t("home.hero.titleLine2")}
          </>
        }
        subtitle={t("home.hero.subtitle")}
      >
        <form
          onSubmit={handleSearch}
          className="bg-white p-1 md:p-2 rounded-full shadow-2xl border border-[#e5d7ca] flex items-center w-full max-w-xl mx-auto"
        >
          <div className="pl-3 md:pl-4 pr-1 md:pr-2 text-[#d8653b] flex-shrink-0">
            <Search size={20} className="md:w-6 md:h-6" />
          </div>

          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("")}
            className="flex-1 h-10 md:h-14 bg-transparent outline-none text-sm md:text-lg px-2 w-0"
          />

          <button
            type="submit"
            className="bg-[#d8653b] text-white px-4 md:px-8 py-2 md:py-4 rounded-full font-bold shadow-lg hover:bg-[#b54e28] transition-all text-sm md:text-base flex-shrink-0"
          >
            {t("home.search.button")}
          </button>
        </form>
      </ParallaxHero>

      <main className="flex-1 px-4 sm:px-6 lg:px-8 py-16 space-y-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

          {/* LEFT: Sidebar (Shortcuts & External Links) */}
          <aside className="lg:col-span-3 space-y-6">
            <div className="sticky top-24 space-y-8">
              
              {/* --- กล่อง Resources@NL.CNX --- */}
              <div>
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <span className="w-2 h-6 bg-[#d8653b] rounded-full"></span>
                  {t("shortcuts.title", "Resources@NL.CNX")}
                </h3>
                <nav className="flex flex-col gap-2">
                  {shortcuts.map((item) => (
                    <button
                      key={item.label}
                      onClick={() => navigate(item.path)}
                      className="text-left px-4 py-3 rounded-xl bg-white border border-[#e5d7ca] hover:bg-[#f1e6da] hover:text-[#d8653b] hover:border-[#d8653b] transition-all text-sm font-medium shadow-sm flex items-center justify-between group"
                    >
                      {item.label}
                      <ArrowRight size={14} className="opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all text-[#d8653b]" />
                    </button>
                  ))}
                </nav>
              </div>

              {/* --- กล่อง ฐานข้อมูลภายนอก (เรียกใช้ Component ใหม่) --- */}
              <ExternalLinks />

            </div>
          </aside>

          {/* CENTER: Main Feed */}
          <section className="lg:col-span-6 space-y-8">

            {/* --- เอกสารใบลาน --- */}
            <div className="lg:col-span-3 space-y-8">
              <div className="flex justify-between items-end mb-4">
                <h3 className="text-xl font-bold mb-3 italic flex items-center gap-2">
                  {t("home.sections.palmLeafRecommended")}
                </h3>
              </div>
              <div className="flex overflow-x-auto gap-4 pb-2 snap-x snap-mandatory hide-scrollbar">
                {palmLeaves.length > 0 ? palmLeaves.map((item, idx) => (
                  <div key={item?.["o:id"] || idx} className="w-[50%] md:w-[50%] snap-start shrink-0">
                    <ManuscriptCard
                      item={item}
                      onClick={() => openDetail(item)}
                      variant="palmleaf"
                      className="h-full"
                    />
                  </div>
                )) : (
                  <div className="text-sm text-gray-400 h-full flex items-center justify-center w-full bg-gray-50 rounded-xl border border-dashed">
                    {t("home.empty.palmLeaf")}
                  </div>
                )}
              </div>
            </div>

            {/* --- หนังสือสมุดไทย --- */}
            <div className="lg:col-span-3 space-y-8">
              <div className="flex justify-between items-end mb-4">
                <h3 className="text-xl font-bold mb-3 italic flex items-center gap-2">
                  {t("home.sections.samutThaiRecommended")}
                </h3>
              </div>
              <div className="flex overflow-x-auto gap-4 pb-2 snap-x snap-mandatory hide-scrollbar h-[400px]">
                {samutThais.length > 0 ? samutThais.map((item, idx) => (
                  <div key={item?.["o:id"] || idx} className="w-full h-full snap-start shrink-0">
                    <ManuscriptCard
                      item={item}
                      onClick={() => openDetail(item)}
                      variant="palmleaf"
                      className="h-full"
                    />
                  </div>
                )) : (
                  <div className="text-sm text-gray-400 h-full flex items-center justify-center w-full bg-gray-50 rounded-xl border border-dashed">
                    {t("home.empty.samutThai")}
                  </div>
                )}
              </div>
            </div>

            <Suspense fallback={<div className="h-96 bg-gray-100 animate-pulse rounded-2xl" />}>
              <ArticlesBlock
                title={t("home.sections.latestActivities")}
                items={articles}
                loading={loading}
                onOpen={openDetail}
              />
            </Suspense>
          </section>

          {/* RIGHT: Recommended */}
          <aside className="lg:col-span-3 space-y-8">
            <div className="sticky top-24">
              <div className="bg-[#5b4a3e] rounded-2xl p-6 text-white shadow-lg relative overflow-hidden group hover:shadow-2xl transition-all duration-500">
                <div
                  className="absolute inset-0 opacity-10"
                  style={{ backgroundImage: "url('/assets/pattern.png')" }}
                />
                <div className="relative z-10">
                  <h3 className="text-xl font-bold mb-3 italic flex items-center gap-2">
                    <Book size={18} /> {t("home.sections.recommendedBooks")}
                  </h3>
                  <Suspense fallback={<div className="h-40 bg-white/10 animate-pulse rounded-lg" />}>
                    <AutoCarousel items={books} onOpen={openDetail} />
                  </Suspense>
                </div>
                <div className="absolute -bottom-4 -right-4 text-white/5 text-8xl font-serif select-none group-hover:scale-110 transition-transform">
                  {t("home.decorative.thaiNumberOne")}
                </div>
              </div>
            </div>
          </aside>

        </div>
      </main>

      <Footer />
    </div>
  );
}